import type { MutableRefObject } from 'react';
import type { IClientOptions, MqttClient } from 'mqtt';

/** The refs the connection logic shares, so callbacks always read the latest values. */
export interface EmqxConnectionRefs {
    emqxClientRef: MutableRefObject<MqttClient | null>;
    isEmqxConnectedRef: MutableRefObject<boolean>;
    emqxUsernameRef: MutableRefObject<string>;
    emqxConnectingRef: MutableRefObject<boolean>;
    emqxRetryCountRef: MutableRefObject<number>;
    emqxRetryTimeoutRef: MutableRefObject<ReturnType<typeof setTimeout> | null>;
    connectToEmqxRef: MutableRefObject<() => Promise<void>>;
    fetchEmqxTokenRef: MutableRefObject<() => Promise<void>>;
}

export interface EmqxConnectAttempt {
    url: string;
    clientId: string;
    options: IClientOptions;
}

/**
 * Broker URL, a fresh client id and the connect options for one attempt. `username` must
 * match exactly what's in the JWT token (returned by backend).
 */
export function emqxConnectOptions(token: string, username: string): EmqxConnectAttempt {
    // Use the hostname that matches the certificate (browsers cannot bypass cert verification)
    const url = 'wss://emqx.optoscale.no:8084/mqtt';
    const clientId = `emqx_frontend_${Math.random().toString(16).substr(2, 8)}`;

    if (!username || username === 'optoscale_frontend') {
        console.warn(
            '[EMQX] Username not set from token response, using default. This may cause authentication issues.'
        );
    }

    const options = {
        username: username,
        password: token,
        clientId: clientId,
        reconnectPeriod: 0, // Disable auto-reconnect, we'll handle it manually
        connectTimeout: 30 * 1000,
        // WebSocket specific options
        protocolVersion: 4 as const, // Use MQTT 3.1.1 for better compatibility
        clean: true, // Clean session
    };

    return { url, clientId, options };
}

interface EmqxHandlerContext {
    url: string;
    clientId: string;
    token: string;
    connectionTimeout: ReturnType<typeof setTimeout>;
    refs: EmqxConnectionRefs;
    setIsEmqxConnected: (connected: boolean) => void;
}

/**
 * Wires one client's connect, disconnect, error, close and offline events: connection state,
 * retries with backoff and a token refresh on an auth failure.
 */
export function attachEmqxHandlers(mqttClient: MqttClient, context: EmqxHandlerContext): void {
    const { url, clientId, token, connectionTimeout, refs, setIsEmqxConnected } = context;
    const {
        emqxClientRef,
        isEmqxConnectedRef,
        emqxUsernameRef,
        emqxConnectingRef,
        emqxRetryCountRef,
        emqxRetryTimeoutRef,
        connectToEmqxRef,
        fetchEmqxTokenRef,
    } = refs;

    mqttClient.on('connect', () => {
        clearTimeout(connectionTimeout);
        emqxConnectingRef.current = false;
        emqxRetryCountRef.current = 0; // Reset retry count on successful connection
        setIsEmqxConnected(true);
        isEmqxConnectedRef.current = true;
        console.log('[EMQX] Connected to MQTT broker');
    });

    mqttClient.on('disconnect', () => {
        clearTimeout(connectionTimeout);
        setIsEmqxConnected(false);
        isEmqxConnectedRef.current = false;
        console.log('[EMQX] Disconnected from MQTT broker');
    });

    mqttClient.on('error', (err) => {
        clearTimeout(connectionTimeout);
        emqxConnectingRef.current = false;
        console.error('[EMQX] MQTT connection error:', err);

        // Handle specific error types
        if (err.message?.includes('connack timeout') || err.message?.includes('timeout')) {
            console.error('[EMQX] Connection timeout - possible causes:');
            console.error('  1. Broker unreachable or network issue');
            console.error('  2. Token invalid or expired');
            console.error('  3. Firewall blocking WebSocket connection');
            console.error('  4. Wrong broker URL or port');
            console.error('[EMQX] Connection details:', {
                url,
                clientId,
                username: emqxUsernameRef.current,
                tokenLength: token.length,
                tokenPrefix: token.substring(0, 20) + '...',
            });
            // Don't set connected state on timeout
            setIsEmqxConnected(false);
            isEmqxConnectedRef.current = false;

            // Retry with exponential backoff (max 3 retries)
            const maxRetries = 3;
            if (emqxRetryCountRef.current < maxRetries) {
                emqxRetryCountRef.current += 1;
                const retryDelay = Math.min(1000 * 2 ** (emqxRetryCountRef.current - 1), 10000); // Exponential backoff, max 10s
                console.log(
                    `[EMQX] Retrying connection in ${retryDelay}ms (attempt ${emqxRetryCountRef.current}/${maxRetries})`
                );

                // Clear any existing retry timeout
                if (emqxRetryTimeoutRef.current) {
                    clearTimeout(emqxRetryTimeoutRef.current);
                }

                // Use ref for recursive call to get latest function reference
                emqxRetryTimeoutRef.current = setTimeout(() => {
                    connectToEmqxRef.current();
                }, retryDelay);
            } else {
                console.error(
                    '[EMQX] Max retries reached. EMQX connection will remain unavailable.'
                );
            }
        } else if (
            err.message?.includes('Not authorized') ||
            err.message?.includes('unauthorized')
        ) {
            console.error('[EMQX] Authentication failed - token may be invalid or expired');
            console.error('[EMQX] Attempting to refresh token...');
            // Try to refresh token and reconnect using refs
            fetchEmqxTokenRef.current().then(() => {
                emqxRetryCountRef.current = 0; // Reset retry count for auth errors
                setTimeout(() => connectToEmqxRef.current(), 1000);
            });
        }
    });

    mqttClient.on('close', () => {
        clearTimeout(connectionTimeout);
        emqxConnectingRef.current = false;
        setIsEmqxConnected(false);
        isEmqxConnectedRef.current = false;
        // reconnectPeriod is 0, so an unexpected close (network drop, socket closed
        // before CONNACK) would otherwise stay offline until a page reload. A client
        // that was replaced or ended on purpose is no longer the current ref.
        if (emqxClientRef.current !== mqttClient) {
            return;
        }
        if (emqxRetryTimeoutRef.current) {
            clearTimeout(emqxRetryTimeoutRef.current);
        }
        const retryDelay = Math.min(1000 * 2 ** emqxRetryCountRef.current, 30_000);
        emqxRetryCountRef.current += 1;
        emqxRetryTimeoutRef.current = setTimeout(() => {
            emqxRetryTimeoutRef.current = null;
            connectToEmqxRef.current();
        }, retryDelay);
    });

    mqttClient.on('offline', () => {
        clearTimeout(connectionTimeout);
        emqxConnectingRef.current = false;
        setIsEmqxConnected(false);
        isEmqxConnectedRef.current = false;
        console.log('[EMQX] Client went offline');
    });
}
